/**
 * Bidirectional conversion between Czech bank account numbers and IBAN.
 *
 * Czech IBAN structure (24 chars):
 *   CZ + 2 check digits + 4 bank code + 6 prefix + 10 account number
 *
 * Czech account format:
 *   [prefix-]accountNumber/bankCode
 *
 * @module banking/conversion
 */

import { IBAN } from 'ibankit';
import { parseCzechBankAccount, type CzechBankAccountParts } from './validation';
import { generateIBAN } from './generation';
import { CountryCode } from 'ibankit';

// ---------------------------------------------------------------------------
// Czech account → IBAN
// ---------------------------------------------------------------------------

export interface CzechToIBANResult {
  iban: string;
  formatted: string;
}

/**
 * Convert a Czech bank account number to IBAN.
 *
 * @param account - Czech account in format `[prefix-]accountNumber/bankCode` or `[prefix-]accountNumber` (when bankCode is provided separately)
 * @param bankCode - Optional 4-digit bank code. When provided, overrides the bank code from account string (and `/bankCode` suffix is not required).
 * @returns IBAN string or `null` if the input is not a valid Czech account format
 *
 * @example
 * ```ts
 * czechAccountToIBAN('19-2000145399/0800')
 * // => { iban: 'CZ6508000000192000145399', formatted: 'CZ65 0800 0000 1920 0014 5399' }
 *
 * czechAccountToIBAN('19-2000145399', '0800')
 * // => { iban: 'CZ6508000000192000145399', formatted: 'CZ65 0800 0000 1920 0014 5399' }
 * ```
 */
export function czechAccountToIBAN(account: string, bankCode?: string): CzechToIBANResult | null {
  // When bankCode is provided separately, append it so parseCzechBankAccount can parse it
  const normalised = bankCode && !account.includes('/') ? `${account.trim()}/${bankCode}` : account;

  const parts = parseCzechBankAccount(normalised);
  if (!parts) return null;

  const resolvedBankCode = bankCode ?? parts.bankCode;
  const accountNumber = parts.accountNumber.padStart(10, '0');
  const prefix = (parts.prefix ?? '0').padStart(6, '0');

  const iban = generateIBAN(CountryCode.CZ, resolvedBankCode, accountNumber, prefix);

  return {
    iban,
    formatted: formatIBAN(iban)
  };
}

// ---------------------------------------------------------------------------
// IBAN → Czech account
// ---------------------------------------------------------------------------

/**
 * Convert a Czech IBAN to a Czech bank account number.
 *
 * @param iban - A valid Czech IBAN (starting with CZ)
 * @returns Parsed Czech account parts and formatted string, or `null` if not a valid Czech IBAN
 *
 * @example
 * ```ts
 * ibanToCzechAccount('CZ6508000000192000145399')
 * // => { prefix: '19', accountNumber: '2000145399', bankCode: '0800', formatted: '19-2000145399/0800' }
 *
 * ibanToCzechAccount('CZ6508000000002000145399')
 * // => { accountNumber: '2000145399', bankCode: '0800', formatted: '2000145399/0800' }
 * ```
 */
export function ibanToCzechAccount(
  iban: string
): (CzechBankAccountParts & { formatted: string }) | null {
  const clean = iban.replace(/\s/g, '').toUpperCase();

  if (!IBAN.isValid(clean)) return null;

  const parsed = new IBAN(clean);
  if (parsed.getCountryCode() !== 'CZ') return null;

  const bban = clean.substring(4); // remove "CZxx"
  // BBAN structure for CZ: bankCode(4) + prefix(6) + accountNumber(10)
  const bankCode = bban.substring(0, 4);
  const rawPrefix = bban.substring(4, 10);
  const rawAccountNumber = bban.substring(10, 20);

  const prefix = stripLeadingZeros(rawPrefix);
  const accountNumber = stripLeadingZeros(rawAccountNumber);

  const hasPrefix = prefix !== '0';
  const parts: CzechBankAccountParts = {
    accountNumber,
    bankCode,
    ...(hasPrefix ? { prefix } : {})
  };

  return {
    ...parts,
    formatted: formatCzechAccount(parts)
  };
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatIBAN(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

function formatCzechAccount(parts: CzechBankAccountParts): string {
  const base = parts.prefix ? `${parts.prefix}-${parts.accountNumber}` : parts.accountNumber;
  return `${base}/${parts.bankCode}`;
}

function stripLeadingZeros(value: string): string {
  const stripped = value.replace(/^0+/, '');
  return stripped || '0';
}
